import React, { memo, useContext, useState } from "react";
import { Modal, Button, Tabs, Space, Checkbox } from "antd";
import { WidgetCard } from "@components/widgets";
import RevinitiAntTableWidget from "@components/widgets/RevinitiAntTableWidget";
import { BCC_MODAL_TYPES, IBCCModalConfig, ICampaignCreationContext } from "@ts/common-types";
import HeaderSection from "./HeaderSection";
import ModalTitleFilters from "./ModalTitleFilters";
import { useModalConfig } from "@/hooks/modal-configs";
import { campaignCreationContext } from "@/ts/constants";

interface ICampaignCreationModalProps {
    modalType: BCC_MODAL_TYPES;
    isVisible: boolean;
    tableContainerRef?: React.RefObject<HTMLDivElement>;
}

export const CampaignCreationModal = memo<ICampaignCreationModalProps>(
    ({ modalType, isVisible, tableContainerRef }) => {

        const context = useContext(campaignCreationContext);
        const config = useModalConfig(modalType, context as ICampaignCreationContext, tableContainerRef);
        const {
            ui,
            header,
            tabs,
            tableConfig,
            handlers,
            validation,
            footer,
            loading,
            metadata,
        } = config;

        const [activeTab, setActiveTab] = useState(
            tabs?.[0]?.key || "default"
        );
        
        const getDataByKey = (key: string) => {
            return metadata?.contextData?.[key] || [];
        };

        const renderHeader = () => {
            if (!header) return null;
            
            if (header.showCheckboxFilters) {
                return <ModalTitleFilters moduleType={config.moduleType} />;
            }

            const iconStyles = header.iconStyles || {};
            
            return (
                <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                    {header.icon && (
                        <div
                            className="d-flex align-items-center justify-content-center rounded"
                            style={{
                                width: iconStyles.containerWidth || "32px",
                                height: iconStyles.containerHeight || "32px",
                                backgroundColor: iconStyles.containerBgColor || "#f5f5f5",
                            }}
                        >
                            {React.isValidElement(header.icon)
                                ? React.cloneElement(header.icon as React.ReactElement, {
                                      style: {
                                          fontSize: iconStyles.iconSize || "18px",
                                          color: iconStyles.iconColor || "#252b37",
                                      },
                                  })
                                : header.icon}
                        </div>
                    )}
                    <span
                        style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#252b37",
                        }}
                    >
                        {header.title}
                    </span>
                </div>
            );
        };

        const renderTabContent = (tab: any) => {
            if (tab.content.type === "custom") {
                return (
                    <div className="campaign-creation-modal-content-wrapper">
                        {tab.content.customComponent}
                    </div>
                );
            }
            return (
                <div className="campaign-creation-modal-content-wrapper">
                    <WidgetCard bodyPaddingClass="px-0 py-0">
                        <RevinitiAntTableWidget
                            config={tab.content.tableConfig}
                            widgetData={{
                                tableData: tab.content.dataSource,
                            }}
                            isLoading={tab.content.isLoading}
                            onTableSelection={tab.handlers?.onTableSelection}
                            selectedRows={tab.content.selectedRows}
                            style={{ height: ui.contentHeight }}
                            dataSorter={tab.content.dataSorter || metadata?.contextData?.dataSorter}
                        />
                    </WidgetCard>
                </div>
            );
        };

        const renderTabExtraContent = () => {
            if (!tabs) return null;
            const currentTab = tabs.find((tab) => tab.key === activeTab);
            return currentTab?.extraContent || null;
        };

        const renderContent = () => {
            // Single table mode
            if (!ui.showTabBar) {
                return (
                    <div className="campaign-creation-modal-content-wrapper">
                        <WidgetCard bodyPaddingClass="px-0 py-0">
                            {ui.showHeaderInput && <HeaderSection />}
                            <RevinitiAntTableWidget
                                config={tableConfig.primary}
                                widgetData={{
                                    tableData: getDataByKey(tableConfig.dataSource.key),
                                }}
                                isLoading={loading?.isLoading}
                                onTableSelection={handlers.onTableSelection}
                                onRowSelect={handlers.onRowSelect}
                                dataFilter={metadata?.contextData?.dataFilter}
                                dataSorter={metadata?.contextData?.dataSorter}
                                style={{ height: ui.contentHeight, justifyHeaderContent: ui.tableHeaderJustifyContent }}
                            />
                        </WidgetCard>
                    </div>
                );
            }

            // Tabs mode (Keywords)
            return (
                <Tabs
                    className="campaign-creation-tabs"
                    activeKey={activeTab}
                    onChange={(activeKey) => setActiveTab(activeKey)}
                    tabBarExtraContent={renderTabExtraContent()}
                    items={tabs?.map((tab) => ({
                        key: tab.key,
                        label: tab.label,
                        children: renderTabContent(tab),
                    }))}
                />
            );
        };

        // ─── Render Footer ───
        const renderFooter = () => {
            const saveDisabled =
                typeof validation?.saveDisabled === "function"
                    ? validation.saveDisabled()
                    : validation?.saveDisabled;

            return (
                <div className="footer">
                    {footer.showSelectedCount && (
                        <div className="selected-items-display">
                            {typeof footer.selectedCountLabel === "function"
                                ? footer.selectedCountLabel(
                                      metadata?.selectedRows?.length || 0
                                  )
                                : footer.selectedCountLabel}
                        </div>
                    )}
                    <Button
                        type="primary"
                        onClick={handlers.onSave}
                        disabled={saveDisabled}
                        loading={footer.saveButtonLoading}
                    >
                        {footer.saveButtonText}
                    </Button>
                </div>
            );
        };

        return (
            <Modal
                title={renderHeader()}
                open={isVisible}
                onCancel={handlers.onCancel}
                width={ui.modalWidth}
                height={ui.modalHeight}
                centered
                footer={null}
                style={{ top: -30 }}
                styles={{
                    body: {
                        padding: 0,
                        height: ui.modalHeight,
                    },
                }}
                wrapClassName="campaign-creation-modal"
            >
                {renderContent()}
                {renderFooter()}
            </Modal>
        );
    }
);
